const computeProminentIdentifier = (advice) => {
    const priority = ['CVE', 'GHSA', 'OSV', 'MAL', 'MET']
    const getPrefix = (id) => id.split('-')[0].toUpperCase()
    const getPriority = (id) => priority.indexOf(getPrefix(id))

    const metId = 'MET-' + advice.id.split('-')[0]
    const raw = [...(advice.identifiers || []), metId]
    if (advice.cve && advice.cve !== "N/A") raw.unshift(advice.cve)
    const identifiers = [...new Set(
        raw.map(id => id.toUpperCase())
           .filter(id => priority.includes(getPrefix(id)))
    )]

    identifiers.sort((a, b) => getPriority(a) - getPriority(b))
    return identifiers[0]
}

class SecurityProcessor {
    constructor(security, jiraInfo) {
        this.security = security
        this.jiraInfo = jiraInfo
        this.map = {}
        this.advisoriesMappedByCve = {}
        this.summary = {
            byLanguage: {},
            issues: {
                cwes: {}, cves: {}, threats: { critical: 0, high: 0, medium: 0, low: 0, suggest: 0 },
                fixable: 0, notFixable: 0, highestCvss: 0, processedAdvisories: []
            }
        }
        this.tree = {}
        this.issues = { vulnerabilities: 0, vulnerableComponents: 0, exclusions: 0 }
        this.section = { summary: [], standard: { showFilters: false, advisories: 0 } }
    }

    process() {
        for (let i = 0; i < this.security.assessments.length; i++) {
            const assessment = this.security.assessments[i]
            this.addLanguageEverywhere(assessment)
            this.sortReports(assessment)
            for (let j = 0; j < assessment.reports.length; j++) {
                const report = assessment.reports[j]
                this.handleSpecialLibraries(report)
                this.sortAdvisories(assessment.reports[j])
                const reportExclusions = report.advices.reduce((exclusions, advice) => {
                    if (advice.exclusions && advice.exclusions.length > 0) exclusions++
                    return exclusions
                }, 0)
                this.addEntryEverywhere(assessment, report, j, reportExclusions)
                for (const advice of report.advices) {
                    if (!advice.exclusions) {
                        advice.exclusions = []
                    }
                    this.addJiraEntryToAdvice(advice)
                    this.addVulnLinkToAdvice(advice)
                    this.addProminentIdentifierToAdvice(advice)
                    this.addAdvisoryToAdvisoriesGroupedByCVE(assessment.language, advice, report)
                    this.summaryAdviceData(advice)
                    this.incrementIssuesCount(report, advice, assessment.language)
                }
            }


            this.addTrustedComponentToIssuesCount(assessment)
        }
        this.sortAdvisoriesGroupedByCve()
        return this.dump()
    }

    handleSpecialLibraries(report){
        report.dependency.library.displayName = report.dependency.library.name
        report.dependency.library.unityPlugin = report.dependency.library.name.toLowerCase().startsWith("upn:")
        if(report.dependency.library.unityPlugin){
            report.dependency.library.displayName = report.dependency.library.name.replace("UPN:", "")
        }
    }

    addJiraEntryToAdvice(advice){
        if(!this.jiraInfo) return
        if (advice.cve) advice.jira = this.jiraInfo.issues.byCve[advice.cve]
        else advice.jira = this.jiraInfo.issues.byAdvice[advice.id]

        if(advice.jira) advice.jira.url = this.jiraInfo.baseurl + "/jira/software/c/projects/" + this.jiraInfo.projectKey +"/issues/" + advice.jira.key
    }

    addVulnLinkToAdvice(advice) {
        if (!advice.links) advice.links = []
        advice.links.unshift({ type: "INFO", url: `${window.location.origin}/vulns?id=${advice.id}`, cve: null, cvetype: false })
        const seen = new Set()
        advice.links = advice.links.filter(link => seen.has(link.url) ? false : seen.add(link.url))
    }

    addProminentIdentifierToAdvice(advice) {
        advice.prominentIdentifier = computeProminentIdentifier(advice)
    }

    dump() {
        return {
            security: this.security,
            map: this.map,
            summary: this.summary,
            tree: this.tree,
            issues: this.issues,
            section: this.section,
            advisoriesMappedByCve: this.advisoriesMappedByCve
        }
    }
    addLanguageEverywhere(assessment) {
        const language = assessment.language
        //MAP
        if (!Object.keys(this.map).includes(language)) {
            this.map[language] = {}
        }

        //SUMMARY
        if (!Object.keys(this.summary).includes(language)) {
            const componentsCount = assessment.componentsCount
            this.summary.byLanguage[language] = { critical: 0, high: 0, medium: 0, low: 0, suggest: 0, none: 0, exclusions: 0, components: !isNaN(componentsCount) ? componentsCount : null }
        }

        //TREE
        if (!Object.keys(this.tree).includes(language)) {
            this.tree[language] = []
        }

        //SECTION SUMMARY
        this.section.summary.push({ language, "empty": true, "issues": [] })
    }
    addAdvisoryToAdvisoriesGroupedByCVE(language, advice, report) {
        if (!advice.cve) {
            this.advisoriesMappedByCve[advice.id] = { ...advice, affectedComponents: [report] }
            return
        }
        if (!this.advisoriesMappedByCve[advice.cve]) {
            this.advisoriesMappedByCve[advice.cve] = { ...advice, affectedComponents: [] }
        }
        this.advisoriesMappedByCve[advice.cve].affectedComponents.push(report)

    }
    getHighestThreatAdviceWithoutExclusion(advisories) {
        for (const advice of advisories) {
            if (!advice.exclusions.length) return advice
        }
        return advisories[0]
    }

    addEntryEverywhere(assessment, report, index, reportExclusions) {
        const language = assessment.language
        const label = report.dependency.library.name + "-" + report.dependency.version
        //MAP
        this.map[language][label] = { index, report }

        //SUMMARY
        const advice = this.getHighestThreatAdviceWithoutExclusion(report.advices)
        if (!advice) return
        const hasExclusions = !!advice.exclusions && advice.exclusions.length > 0

        if (reportExclusions === report.advices.length) { this.summary.byLanguage[language].exclusions++; }
        if (advice.severity.toLowerCase() === "critical" && !hasExclusions) {
            this.summary.byLanguage[language].critical++;
            this.summary.issues.threats.critical++
        }
        if (advice.severity.toLowerCase() === "high" && !hasExclusions) {
            this.summary.byLanguage[language].high++;
            this.summary.issues.threats.high++
        }
        if (advice.severity.toLowerCase() === "medium" && !hasExclusions) {
            this.summary.byLanguage[language].medium++;
            this.summary.issues.threats.medium++
        }
        if (advice.severity.toLowerCase() === "low" && !hasExclusions) {
            this.summary.byLanguage[language].low++;
            this.summary.issues.threats.low++
        }
        if (advice.severity.toLowerCase() === "none" && !hasExclusions) {
            this.summary.byLanguage[language].none++;
            this.summary.issues.threats.suggest++
        }
        if (advice.severity.toLowerCase() === "suggest" && !hasExclusions) {
            this.summary.byLanguage[language].suggest++;
            this.summary.issues.threats.none++
        }



        if (report.safeVersions && (report.safeVersions.latestPatch || report.safeVersions.latestMinor || report.safeVersions.latestMajor)) {
            this.summary.issues.fixable++
        }
        else {
            this.summary.issues.notFixable++
        }

        //ISSUES
        if (report.advices.length) {
            this.issues.vulnerableComponents++
        }

        //TREE
        const componentHighestThreat = advice.severity.toLowerCase()
        let highestThreat = componentHighestThreat, highestThreatText = componentHighestThreat
        if (componentHighestThreat === "medium") { highestThreat = "medium"; highestThreatText = "medium-alt" }
        if (componentHighestThreat === "low") { highestThreat = "low"; highestThreatText = "low-alt" }

        this.tree[language].push({ ...report, highestThreat, highestThreatText })

        //SECTION SUMMARY
        const idx = this.section.summary.length - 1
        this.section.summary[idx].empty = false
        this.section.summary[idx].issues.push({
            name: report.dependency.library.name,
            version: report.dependency.version,
            threat: advice.severity,
            hasSafeVersion: report.safeVersions ? !!report.safeVersions.latestPatch || !!report.safeVersions.latestMinor || !!report.safeVersions.latestMinor : null
        })

        //SECTION STANDARD
        this.section.standard.showFilters = true
    }

    summaryAdviceData(advice) {
        let cve = advice.cve || "N/A";
        if (!Object.keys(this.summary.issues.cves).includes(cve)) {
            this.summary.issues.cves[cve] = 0
        }
        this.summary.issues.cves[cve]++;

        if (this.summary.issues.processedAdvisories.includes(advice.id)) {
            return
        }
        this.summary.issues.processedAdvisories.push(advice.id)

        let currentCwe = advice.cwe || "NO-CWE"
        if (!Object.keys(this.summary.issues.cwes).includes(currentCwe)) {
            this.summary.issues.cwes[currentCwe] = 0
        }
        this.summary.issues.cwes[currentCwe]++
        if (advice.cvss > this.summary.issues.highestCvss) {
            this.summary.issues.highestCvss = advice.cvss
        }


    }


    incrementIssuesCount(report, advice, language) {
        this.issues.vulnerabilities++
        if (!Object.keys(report).includes("exclusions")) {
            report.exclusions = 0
        }
        if (advice.exclusions.length) {
            this.issues.exclusions++
            report.exclusions++
        }
    }

    addTrustedComponentToIssuesCount(assessment) {
        if (!assessment.trusted) return
        this.issues.exclusions += assessment.trusted.length
    }

    sortReports(assessment) {
        assessment.reports.sort((a, b) => {
            return a.dependency.library.name.toLowerCase() + ":" + a.dependency.version > b.dependency.library.name.toLowerCase() + ":" + b.dependency.version ? -1 : 1
        })
    }
    sortFunction(a, b) {
        if (b.severity === "CRITICAL") return 1;
        if (a.severity === "CRITICAL") return -1;
        if (b.severity === "HIGH") return 1;
        if (a.severity === "HIGH") return -1;
        if (b.severity === "MEDIUM") return 1;
        if (a.severity === "MEDIUM") return -1;
        if (b.severity === "LOW") return 1;
        if (a.severity === "LOW") return -1;
        if (b.severity === "SUGGEST") return 1;
        if (a.severity === "SUGGEST") return -1;
        if (b.severity === "NONE") return 1;
        if (a.severity === "NONE") return -1;
        return -1
    }
    sortAdvisories(report) {
        report.advices.sort((a, b) => this.sortFunction(a, b))
    }

    sortAdvisoriesGroupedByCve() {
        const entries = Object.entries(this.advisoriesMappedByCve)
        entries.sort((a, b) => this.sortFunction(a[1], b[1]))
        this.advisoriesMappedByCve = Object.fromEntries(entries)
    }

}
class StabilityProcessor {
}
class LicensingProcessor {
    constructor(licensing) {
        this.licensing = licensing
        this.summary = {
            byLanguage: {},
            issues: {}
        }
        this.tree = {}
        this.issues = 0
        this.section = {
            summary: {},
            standard: [],
            legal: [],
            licenses: {},
            unity: false
        }


        this.licenseNotFound = { id: "NOT DECLARED", name: "NOT DECLARED / NOT FOUND", warnings: "No valid license found!", urls: ["https://en.wikipedia.org/wiki/Software_license"] }
    }

    addLanguageEverywhere(report) {
        const componentsCount = report.componentsCount, language = report.language
        //SUMMARY
        this.summary.byLanguage[language] = { tot: 0, undeclared: 0, forbidden: 0, undeclaredWithoutExclusion: 0, forbiddenWithoutExclusion: 0, violationExclusions: 0, warningExclusions: 0, components: !isNaN(componentsCount) ? componentsCount : null }
        this.summary.byLanguage[language].tot = report.results.length

        //TREE
        this.tree[language] = []


        //LEGAL
        this.section.legal.push({ language, licenses: [] })



    }

    addEntryEverywhere(report, result, resultLicense, percentage) {


        const language = report.language
        //SUMMARY
        if (result.violations.length) {
            this.summary.byLanguage[language].forbidden++
            this.summary.byLanguage[language].forbiddenWithoutExclusion++
        }
        if (result.warnings.length) {
            this.summary.byLanguage[language].undeclared++
            this.summary.byLanguage[language].undeclaredWithoutExclusion++
        }
        if (result.exclusions && result.exclusions.length > 0) {
            if (result.violations.length) {
                this.summary.byLanguage[language].violationExclusions++
                this.summary.byLanguage[language].forbiddenWithoutExclusion--
            }
            if (result.warnings.length) {
                this.summary.byLanguage[language].warningExclusions++
                this.summary.byLanguage[language].undeclaredWithoutExclusion--
            }
        }


        //TREE
        if (result.warnings.length || result.violations.length) {
            this.tree[language].push(result)
        }

        //ISSUES
        if (result.warnings.length || result.violations.length) {
            this.issues++
        }

        //LEGAL
        for (const license of resultLicense) {
            if (!Object.keys(this.section.licenses).includes(license.id)) {
                this.section.licenses[license.id] = { ...license, results: [], hasWarning: false, hasViolation: false, violations: [], warnings: [], exclusions: [] }
            }
            this.section.licenses[license.id].results.push(result)
            if (result.violations.length) {
                this.section.licenses[license.id].hasViolation = true
                this.section.licenses[license.id].violations = [...this.section.licenses[license.id].violations, ...result.violations]
            }
            if (result.warnings.length) {
                this.section.licenses[license.id].hasWarning = true
                this.section.licenses[license.id].warnings = [result.warnings]
            }
            if (result.exclusions && result.exclusions.length) {
                this.section.licenses[license.id].hasExclusion = true
                this.section.licenses[license.id].exclusions = [...this.section.licenses[license.id].exclusions, ...result.exclusions]
            }
        }

        //UNITY
        let hasUnityLicense = false
        for (const license of result.licenses) {
            if (license.id === "UNITY") {
                hasUnityLicense = true
                break
            }
        }
        result.unity = hasUnityLicense

        //BLURRED
        result.blurred = {}
        result.canBeBlurred = percentage > 10
        this.processDataForOpensourceAccount(result)

    }
    blurName(name) {
        return name.split("").map((letter) => { return this.findLetterReplacement(letter) }).toString().replace(/,|\/|;/g, "").replace(/\./g, "fk");
    }
    findLetterReplacement(letter) {
        return String.fromCharCode(letter.charCodeAt() + 1);
    }
    blurVersion(version) {
        if (!version) return null;
        const v = version.split(".").map((number) => { return number * 1 + 2 }).toString().replace(/,/g, "");
        return v
    }


    processDataForOpensourceAccount(item) {
        item.blurred.version = this.blurVersion(item.version);
        item.blurred.library = { ...item.library, name: this.blurName(item.library.name) };
        item.blurred.blurred = true;
        item.blurred.link = "#";
        item.blurred.warnings = [];
        item.blurred.violations = [];
        item.blurred.exclusion = true;
        item.blurred.licenses = [{ id: "NONE", name: "None", link: "#", wildcard: false, proprietary: true }];
    }

    sortComponents(report) {
        report.results.sort((a, b) => a.library.name.toLowerCase() + ":" + a.version > b.library.name.toLowerCase() + ":" + b.version ? -1 : 1)
    }

    dump() {
        return {
            licensing: this.licensing,
            summary: this.summary,
            tree: this.tree,
            issues: this.issues,
            section: this.section
        }
    }

    process() {
        this.section.licenses = {}
        for (let i = 0; i < this.licensing.reports.length; i++) {
            const report = this.licensing.reports[i]
            this.addLanguageEverywhere(report)
            let warningsCount = 0, violationsCount = 0

            for (let j = 0; j < report.results.length; j++) {
                const result = report.results[j]
                this.handleSpecialLibraries(result)
                warningsCount += result.warnings.length
                violationsCount += result.violations.length

                if (result.version === "--") result.version = ""
                const resultLicenses = result.licenses.slice()
                if (!resultLicenses.length) resultLicenses.push(this.licenseNotFound)

                this.addEntryEverywhere(report, result, resultLicenses, (j / report.results.length) * 100)
                if (report.results[j].unity) {
                    this.section.unity = true
                }
            }
            report.warningsCount = warningsCount
            report.violationsCount = violationsCount

            for (const entry of this.section.legal) {
                if (entry.language === report.language) {

                    entry.licenses = Object.values(this.section.licenses)
                    break
                }
            }

        }

        for (const language of this.section.legal) {
            language.licenses.sort((a, b) => {
                if (a.hasViolation) return -1
                if (b.hasViolation) return 1
                if (a.hasWarning) return -1
                if (b.hasWarning) return 1
                return 1
            })
        }

        this.section.standard = this.licensing.reports

        return this.dump()
    }

    handleSpecialLibraries(report){
        report.library.displayName = report.library.name
        report.library.unityPlugin = report.library.name.toLowerCase().startsWith("upn:")
        if(report.library.unityPlugin){
            report.library.displayName = report.library.name.replace("UPN:", "")
        }
    }
}
class ExclusionsProcessor {
    constructor(exclusions, exclusionsInReport) {
        this.exclusionsInReport = exclusionsInReport
        this.exclusions = exclusions
        if (!exclusions) {
            this.exclusions = this.exclusionsInReport
        }

    }

    determineIfSecurityExclusionIsImportable(currentExclusion, report, advice, language) {
        const isTransitive = currentExclusion.transitive
        const isAdvicesSource = currentExclusion.source === "security:advices"
        const isCvesSource = currentExclusion.source === "security:cves"
        const isLibrariesSource = currentExclusion.source === "security:libraries"
        const isSameAdviceUuid = currentExclusion.uuid === advice.id
        const isSameCve = currentExclusion.cve === advice.prominentIdentifier
        const isSameLibrary = currentExclusion.library && currentExclusion.library.name === advice.library.name
        const isSameLanguage = currentExclusion.library && currentExclusion.library.language === language
        const isSameVersion = currentExclusion.version === "*" || currentExclusion.version === report.dependency.version
        return {
            advice: !isTransitive && isAdvicesSource && isSameAdviceUuid,
            cve: !isTransitive && isCvesSource && isSameCve,
            library: !isTransitive && isLibrariesSource && isSameLibrary && isSameVersion && isSameLanguage
        }
    }

    determineIfStabilityExclusionIsImportable(currentExclusion, version, language) {
        const isLibrariesExclusion = currentExclusion.source === "stability:libraries"
        if (!isLibrariesExclusion) return false
        const isExclusionInvalidOrIncomplete = !currentExclusion.library
        if (isExclusionInvalidOrIncomplete) return false
        const isSameLibrary = currentExclusion.library.name === version.name
        const isSameVersion = currentExclusion.version === version.version || currentExclusion.version === "*"
        const isSameLanguage = currentExclusion.library.language === language
        return isSameLibrary && isSameVersion && isSameLanguage
    }

    determineIfLicensingExclusionIsImportable(currentExclusion, result, language) {
        const isLibrariesExclusion = currentExclusion.source === "licensing:libraries"
        if (!isLibrariesExclusion) return false
        const isExclusionInvalidOrIncomplete = !currentExclusion.library
        if (isExclusionInvalidOrIncomplete) return false
        const isSameLibrary = currentExclusion.library.name === result.library.name
        const isSameVersion = currentExclusion.version === result.version || currentExclusion.version === "*"
        const isSameLanguage = currentExclusion.library.language === language
        return isSameLibrary && isSameVersion && isSameLanguage
    }


    injectSecurityExclusion(advice, report, language, exclusions) {
        if (!advice.exclusions) advice.exclusions = []
        if (!advice.prominentIdentifier) advice.prominentIdentifier = computeProminentIdentifier(advice)
        const injected = []
        advice.exclusions = advice.exclusions.reduce((processed, exclusion) => {
            if (!Object.keys(exclusions).includes(exclusion)) return processed
            processed.push(exclusions[exclusion])
            injected.push(exclusion)
            return processed
        }, [])
        Object.keys(exclusions).map((exclusionId) => {
            if (injected.includes(exclusionId)) return null
            const currentExclusion = exclusions[exclusionId]
            const isExclusionImportable = this.determineIfSecurityExclusionIsImportable(currentExclusion, report, advice, language)
            if (isExclusionImportable.advice) {
                advice.exclusions.push(currentExclusion)
            }
            if (isExclusionImportable.cve) {
                advice.exclusions.push(currentExclusion)
            }
            if (isExclusionImportable.library) {
                advice.exclusions.push(currentExclusion)
            }
            return null
        })
    }

    injectTrustedComponentAsExclusions(exclusions, report) {
        const main = () => {
            for (const assessment of report.assessments) {
                if (!assessment.trusted) assessment.trusted = []
                for (const id in exclusions) {
                    const exclusion = exclusions[id]
                    if (isTransitiveExclusion(exclusion) && isLibraryExclusion(exclusion) && isSameLanguage(exclusion, assessment.language)) {
                        injectComponentInSecurityAssessment(exclusion, assessment)
                    }
                }
            }
        }

        const addTrustedComponentsSectionToAssessment = (language) => {
            for (const assessment of report.assessments) {
                if (assessment.language === language) {
                    assessment.trusted = []
                    break
                }
            }
        }

        const isTransitiveExclusion = (exclusion) => exclusion.transitive
        const isLibraryExclusion = (exclusion) => !!exclusion.library
        const isSameLanguage = (exclusion, language) => isLibraryExclusion(exclusion) && exclusion.library.language === language
        const injectComponentInSecurityAssessment = (exclusion, assessment) => {
            const componentReport = {
                advices: [],
                dependency: {
                    library: exclusion.library,
                    licenses: [],
                    root: false,
                    version: exclusion.version
                },
                empty: false,
                hierarchy: [],
                locations: [],
                safeVersions: {},
                scope: null,
                transitive: null,
                transitiveExclusions: [exclusion],
                exclusions: 0
            }
            assessment.trusted.push(componentReport)
        }

        main()
    }

    processSecurity(securityReport, exclusions) {
        securityReport.hasExclusions = false
        securityReport.fullyExcluded = false
        let excludedAssessments = 0
        this.injectTrustedComponentAsExclusions(exclusions, securityReport)
        for (const assessment of securityReport.assessments) {
            assessment.excluded = false
            let excludedReports = 0
            const language = assessment.language
            for (const report of assessment.reports) {
                report.excluded = false
                let excludedAdvices = 0
                for (const advice of report.advices) {

                    this.injectSecurityExclusion(advice, report, language, exclusions)
                    if (advice.exclusions.filter(exclusion => !exclusion.transitive).length) {
                        securityReport.hasExclusions = true
                        excludedAdvices++
                    }
                }
                if (excludedAdvices === report.advices.length) {
                    report.excluded = true
                    excludedReports++
                }

            }
            if (assessment.trusted && assessment.trusted.length) {
                securityReport.hasExclusions = true
            }
            if (excludedReports === assessment.reports.length) {
                assessment.excluded = true
                excludedAssessments++
            }
        }

        if (excludedAssessments === securityReport.assessments.length) {
            securityReport.fullyExcluded = true
        }
    }
    processStability(stabilityReport, exclusions) {
        for (const report of stabilityReport.reports) {
            const language = report.language
            for (const version of report.versions) {
                if (!version.exclusions) version.exclusions = []
                version.exclusions = version.exclusions.reduce((processed, exclusion) => {
                    if (!Object.keys(exclusions).includes(exclusion)) return processed
                    processed.push(exclusions[exclusion])
                    return processed
                }, [])
                Object.keys(exclusions).map((exclusionId) => {
                    const currentExclusion = exclusions[exclusionId]
                    const isExclusionImportable = this.determineIfStabilityExclusionIsImportable(currentExclusion, version, language)
                    if (isExclusionImportable) {
                        version.exclusions.push(currentExclusion)
                    }
                    return null
                })
            }
        }
    }
    processLicensing(licensingReport, exclusions) {
        for (const report of licensingReport.reports) {
            const language = report.language
            for (const result of report.results) {
                if (!result.exclusions) result.exclusions = []
                result.exclusions = result.exclusions.reduce((processed, exclusion) => {
                    if (!Object.keys(exclusions).includes(exclusion)) return processed
                    processed.push(exclusions[exclusion])
                    return processed
                }, [])
                Object.keys(exclusions).map((exclusionId) => {
                    const currentExclusion = exclusions[exclusionId]
                    const isExclusionImportable = this.determineIfLicensingExclusionIsImportable(currentExclusion, result, language)
                    if (isExclusionImportable) {
                        result.exclusions.push(currentExclusion)
                    }
                    return null
                })
            }
        }
    }

    compare() {

        for (const entry in this.exclusions) {
            if (this.exclusions[entry].readonly) continue
            if (!Object.keys(this.exclusionsInReport).includes(entry)) {
                return true
            }
        }
        for (const entry in this.exclusionsInReport) {
            if (this.exclusionsInReport[entry].readonly) continue
            if (!Object.keys(this.exclusions).includes(entry)) {
                return true
            }
        }

        return false
    }

    // this.exclusions is already expiry-filtered (it comes from the /api/v2/exclusions
    // `live` partition), but this.exclusionsInReport is read straight off the report body,
    // which never passes through that partition. Re-importing an expired entry from here
    // would put it back in front of injectSecurityExclusion and re-suppress a finding whose
    // account-wide override has lapsed.
    importAccountWideExclusionsFromReportToExclusionsApi() {
        for (const exclusionId in this.exclusionsInReport) {
            const exclusion = this.exclusionsInReport[exclusionId]
            if (exclusion.readonly && !window.ExclusionExpiry.hasExpired(exclusion)) {
                this.exclusions[exclusionId] = exclusion
            }
        }
    }

    process(report) {
        this.importAccountWideExclusionsFromReportToExclusionsApi()
        this.processSecurity(report.security, this.exclusions)
        this.processStability(report.stability, this.exclusions)
        this.processLicensing(report.licensing, this.exclusions)
    }

}

class ReportDataProcessor {
    constructor(report, exclusions, exclusionsInReport) {
        this.report = report

        this.securityProcessor = new SecurityProcessor(report.security, report.jiraInfo)
        this.stabilityProcessor = new StabilityProcessor(report.stability)
        this.licensingProcessor = new LicensingProcessor(report.licensing)
        this.exclusionsProcessor = new ExclusionsProcessor(exclusions, exclusionsInReport)
    }

    process() {
        try {
            this.exclusionsProcessor.process(this.report)
            const areExclusionsDifferent = this.exclusionsProcessor.compare()

            const security = this.securityProcessor.process()
            const licensing = this.licensingProcessor.process()


            return {
                security,
                licensing,
                areExclusionsDifferent
            }
        }
        catch (e) {
            console.log(e)
        }
    }


}


export default ReportDataProcessor